import React, { useEffect, useState, useContext } from "react";

import GenomeCard from "../genome-card";
import Loading from "../loading";
import UnexpectedBackendError from "../backend-error";
import { RelatedAssemblyResult, RelatedAssembliesResult } from "../../api";
import { ApiContext } from "../../pages/_app";

interface Props {
  uuid: string;
}

export function RelatedGenomes(props: Props) {
  const api = useContext(ApiContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasErrored, setHasErrored] = useState<boolean>(false);
  const [neighborsByDist, setNeighborsByDist] = useState<RelatedAssemblyResult[]>([]);
  const [neighborsByTaxa, setNeighborsByTaxa] = useState<RelatedAssemblyResult[]>([]);

  useEffect(() => {
    setLoading(true);

    api
      .get<RelatedAssembliesResult>("/genome-portal-api/related-assemblies/{uuid}", {
        uuid: props.uuid,
      })
      .then((resp) => {
        setLoading(false);

        if (resp.error === undefined) {
          setNeighborsByDist(resp.data.neighbors_by_dist);
          setNeighborsByTaxa(resp.data.neighbors_by_taxa);
        } else {
          setHasErrored(true);
        }
      });
  }, []);

  if (loading) {
    return <Loading text="Loading data..." />;
  }

  if (hasErrored) {
    return <UnexpectedBackendError />;
  }

  function renderNeighborGenomes(neighbors: RelatedAssemblyResult[], keyPrefix: string) {
    return neighbors.map((neighbor) => {
      let similarity = "";
      if (neighbor.pseudo_ani !== undefined && neighbor.pseudo_ani !== null) {
        const percentage = neighbor.pseudo_ani.toFixed(2);
        similarity = `${percentage === "100.00" ? "> 99.99" : percentage}% similar`;
      }
      return (
        <GenomeCard
          key={keyPrefix + neighbor.assembly.genome.uuid}
          assembly={neighbor.assembly}
          similarity={similarity}
        />
      );
    });
  }

  function renderSimilar(neighbors: RelatedAssemblyResult[]) {
    // Only display Finch-based near neighbors if ANI >= 95% (our display threshold)
    const similarNeighbors = neighbors.filter(
      (neighbor) => neighbor.pseudo_ani && neighbor.pseudo_ani >= 95
    );
    return (
      <>
        <h3>Most similar genomes</h3>
        <p>
          {similarNeighbors.length > 0
            ? "The following genomes have the greatest genomic similarity to this one (>95% average nucleotide identity)."
            : "No genomes with >95% similarity found."}
        </p>

        <div className="related-genomes__cards">
          {renderNeighborGenomes(similarNeighbors, "neighbor-")}
        </div>
      </>
    );
  }

  function renderSameGenus(neighbors: RelatedAssemblyResult[]) {
    return (
      <>
        <h3>Other members of this genus</h3>
        <p>
          {neighbors.length > 0
            ? "The following genomes share the same genus according to the NCBI taxonomy."
            : "No genomes found sharing the same genus."}
        </p>

        <div className="related-genomes__cards">{renderNeighborGenomes(neighbors, "taxa-")}</div>
      </>
    );
  }

  return (
    <>
      <div className="related-genomes__container">{renderSimilar(neighborsByDist)}</div>
      <div className="related-genomes__container">{renderSameGenus(neighborsByTaxa)}</div>
    </>
  );
}

export default RelatedGenomes;
